import { useLocation } from 'react-router-dom';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import SectionHeading from '@/components/typography/SectionHeading';
import { analytics } from '@/lib/analytics';

// SEO-Optimized FAQs targeting key search queries
// Aligned with project.ts single source of truth
export const faqs = [
  {
    question: 'Is Nahar Chandi Valley an under-construction project in Chandivali?',
    answer: 'Yes. Nahar Chandi Valley is currently under construction and offers buyers an opportunity to invest early in a premium residential development in Chandivali. The project is MahaRERA registered (P51800002500059) with expected possession in December 2030. View current pricing and availability.',
    link: '/price',
  },
  {
    question: 'What is Nahar Chandi Valley?',
    answer: 'Nahar Chandi Valley is a premium under-construction residential project in Chandivali, near Powai, Mumbai. Developed by Nahar Group, it primarily offers spacious 3 BHK residences designed for end-use buyers, surrounded by a serene Miyawaki forest landscape. Explore the floor plans.',
    link: '/floor-plans',
  },
  {
    question: 'Where is Nahar Chandi Valley located?',
    answer: 'Nahar Chandi Valley is strategically located in Chandivali, near Powai, Mumbai (PIN: 400072). The project enjoys excellent connectivity to JVLR (~3 mins), Sakinaka Metro Station (~5 mins), Mumbai Airport (~25 mins), and major business hubs including Powai IT Park and IIT Bombay (~12 mins). View location details and map.',
    link: '/location',
  },
  {
    question: 'Who is the developer of Nahar Chandi Valley?',
    answer: 'Nahar Chandi Valley is developed by Nahar Group, a legacy developer with decades of excellence in Mumbai real estate, known for quality construction and timely delivery. See RERA details.',
    link: '/rera',
  },
  {
    question: 'What configurations are available at Nahar Chandi Valley?',
    answer: 'The project is primarily planned around spacious 3 BHK residences, designed for end-use buyers prioritising comfort, privacy, and long-term living. Configurations include 2 BHK (886 sq.ft.), 3 BHK (1,068–1,864 sq.ft.), and 4 BHK (2,487 sq.ft.) with Vastu-compliant layouts and large decks.',
  },
  {
    question: 'Does Nahar Chandi Valley offer 2 BHK apartments?',
    answer: 'Nahar Chandi Valley is primarily planned around spacious 3 BHK residences. Buyers exploring 2 BHK options often upgrade after evaluating the long-term usability and value offered by a well-designed 3 BHK home. Limited 2 BHK units (886 sq.ft.) are available in A&B Wing.',
  },
  {
    question: 'How close is Nahar Chandi Valley to Nahar Amrit Shakti?',
    answer: 'The project is located near the Nahar Amrit Shakti neighbourhood, allowing residents to benefit from established social infrastructure including Nahar International School, Nahar Medical Center, and Amrit Shakti Udyan gardens while living in a newer residential development.',
  },
  {
    question: 'What is the price of apartments at Nahar Chandi Valley?',
    answer: 'Apartments at Nahar Chandi Valley start from ₹2.90 Cr* for 2 BHK, 3 BHK from ₹3.49 Cr*, and 4 BHK at ₹8.13 Cr* (approx. ₹32,700/sq.ft.*). Prices vary based on configuration, floor, and view. Contact our sales team for the latest price sheet.',
  },
  {
    question: 'Is Nahar Chandi Valley near Powai?',
    answer: 'Yes, Nahar Chandi Valley is located in Chandivali, adjacent to Powai. The Powai business hub, including Hiranandani Gardens and major IT parks, is just ~10 minutes drive. IIT Bombay is approximately ~12 minutes away.',
  },
  {
    question: 'What amenities are available at Nahar Chandi Valley?',
    answer: 'Nahar Chandi Valley offers world-class amenities including a temperature-controlled swimming pool, state-of-the-art fitness center, landscaped gardens with Miyawaki forest, jogging track, kids play area, indoor games room, yoga deck, multi-purpose hall, podium lounge, senior citizen area, and 24/7 security. (Indicative; subject to final approvals). Explore all amenities.',
    link: '/amenities',
  },
  {
    question: 'What is the RERA registration number for Nahar Chandi Valley?',
    answer: 'Nahar Chandi Valley is registered with MahaRERA under registration number P51800002500059. You can verify this on the official MahaRERA website (maharera.mahaonline.gov.in) for complete project details and disclosures.',
  },
  {
    question: 'How does Nahar Chandi Valley compare with other projects in Chandivali?',
    answer: 'Nahar Chandi Valley is often evaluated for its prime location near Powai, planning efficiency with large decks, township living benefits, and Nahar Group\'s track record. Buyers typically compare connectivity, apartment layouts, amenities, and overall liveability before deciding.',
  },
  {
    question: 'When is the expected possession for Nahar Chandi Valley?',
    answer: 'The expected possession for Nahar Chandi Valley is December 2030, subject to necessary approvals and construction progress. Contact our sales team for the most current timeline and construction updates.',
  },
  {
    question: 'How can I schedule a site visit to Nahar Chandi Valley?',
    answer: 'You can schedule a site visit by calling +91 98201 88150, sending a WhatsApp message, or filling out the enquiry form on this website. Site visits are available 7 days a week from 10:00 AM to 7:00 PM. Our team will arrange a personalized walkthrough. Contact us now.',
    link: '/contact',
  },
  {
    question: 'What are the wing configurations at Nahar Chandi Valley?',
    answer: 'The project has 4 wings: A&B Wing offers 2 BHK and 3 BHK M1 configurations, while C&D Wing offers 3 BHK S, M1, M2, L, and 4 BHK configurations. D Wing features the premium 3 BHK L and flagship 4 BHK units with servant rooms.',
  },
  {
    question: 'Is the project Vastu compliant?',
    answer: 'Yes, all units at Nahar Chandi Valley are designed following Vastu principles to ensure positive energy flow and optimal space utilization.',
  },
  {
    question: 'Are there payment plans available?',
    answer: 'Yes, flexible payment plans are available. Contact our sales team for details on construction-linked plans, subvention schemes, and bank loan assistance.',
  },
  {
    question: 'What is the Gold Assurance at Nahar Chandi Valley?',
    answer: 'The Gold Assurance is a benefit applicable on successful registration of the property, under which purchasers are eligible to receive a gold benefit equivalent to 10 grams of gold. View Gold Assurance details.',
    link: '/gold-assurance',
  },
  {
    question: 'When does the Gold Assurance apply?',
    answer: 'The Gold Assurance applies only after successful registration of the agreement for sale of the property. Registrations must be completed between 14 January 2026 and 31 March 2026.',
  },
  {
    question: 'How is the gold value calculated?',
    answer: 'The value is calculated based on the prevailing 22-carat gold rate for 10 grams, as published on www.goodreturns.in, on the date of registration of the property.',
  },
  {
    question: 'Will I receive a physical gold coin?',
    answer: 'The benefit may be provided either in the form of a physical gold coin or its equivalent monetary value, at the sole discretion of the promoter/advisory partner.',
  },
  {
    question: 'Is the gold value fixed at the time of booking?',
    answer: 'No. The gold value is not fixed at the time of booking. It is calculated only on the date of registration of the property.',
  },
  {
    question: 'Is the Gold Assurance part of the property price?',
    answer: 'No. The Gold Assurance does not form part of the consideration value of the property and is independent of the property price.',
  },
  {
    question: 'Are there any conditions to receive the Gold Assurance?',
    answer: 'Yes. The Gold Assurance is subject to fulfillment of booking and registration milestones and compliance with applicable terms and conditions.',
  },
  {
    question: 'Is Chandivali better than Powai for buying a home?',
    answer: 'Chandivali offers similar connectivity to Powai with the advantage of newer residential developments and relatively lower density. Buyers seeking under-construction projects with modern planning often prefer Chandivali over older Powai developments.',
  },
  {
    question: 'Are there good under-construction flats near Nahar Amrit Shakti?',
    answer: 'Yes. Buyers often explore nearby under-construction projects in Chandivali for newer layouts, phased pricing, and modern amenities. Nahar Chandi Valley is one such option, primarily offering 3 BHK residences for end-use buyers.',
  },
  {
    question: 'Is 3 BHK worth upgrading from a 2 BHK?',
    answer: 'For end-use buyers, a 3 BHK typically offers better long-term livability and flexibility compared to compact 2 BHK units. With under-construction pricing, the gap between configurations is often narrower than expected while the lifestyle upgrade is significant.',
  },
  // Phase 16 - Additional FAQs for query hijacking
  {
    question: 'Is a 2 BHK available in Nahar Amrit Shakti currently?',
    answer: 'Availability of new 2 BHK units in Nahar Amrit Shakti is limited. Buyers often explore newer under-construction 3 BHK homes in Chandivali for better planning and long-term usability.',
  },
  {
    question: 'Why do buyers prefer 3 BHK over 2 BHK in Chandivali?',
    answer: 'Modern 3 BHK layouts offer improved space efficiency, work-from-home flexibility, and future comfort, making them a preferred choice even for nuclear families.',
  },
  {
    question: 'Is Chandivali close to Powai and JVLR?',
    answer: 'Yes. Chandivali enjoys strong connectivity to Powai, JVLR, SEEPZ, and central business districts with minimal travel time.',
  },
  {
    question: 'What is the difference between Nahar Amrit Shakti and Nahar Chandi Valley?',
    answer: 'Nahar Amrit Shakti is an established township with older inventory, while Nahar Chandi Valley is a newer under-construction development with modern 3 BHK layouts, contemporary amenities, and fresh-phase availability.',
  },
  // Miyawaki Forest & Sustainability FAQs
  {
    question: 'What is a Miyawaki forest and why is it significant?',
    answer: 'A Miyawaki forest is a dense, multi-layered native forest created using a rapid reforestation method pioneered by Japanese botanist Akira Miyawaki. It grows 10x faster and 30x denser than conventional plantations, offering superior biodiversity, oxygen output, and micro-climate cooling.',
  },
  {
    question: 'Does Nahar Group have a Miyawaki forest in Chandivali?',
    answer: 'Yes. The Nahar Amrit Shakti Udyan in Chandivali features approximately 34,000 sq. metres of urban forest developed using the Miyawaki methodology, comprising over 41,000 trees across 79 carefully selected native and exotic species.',
  },
  {
    question: 'Is the Nahar Amrit Shakti Udyan nationally recognised?',
    answer: 'Yes. The Nahar Amrit Shakti Udyan was acknowledged by the Hon\'ble Prime Minister of India, Shri Narendra Modi, during Mann Ki Baat as one of the largest Miyawaki forests in Mumbai.',
  },
  {
    question: 'How does sustainable development inform Nahar Chandi Valley planning?',
    answer: 'Nahar Group\'s sustainability philosophy directly informs the planning ethos of Nahar developments. Green infrastructure is treated as permanent urban capital, not cosmetic landscaping, ensuring long-term ecological balance alongside urban development.',
  },
  {
    question: 'What are the environmental benefits of a Miyawaki forest?',
    answer: 'Miyawaki forests provide significantly higher biodiversity, increased oxygen output, effective carbon sequestration, micro-climate cooling, noise reduction, and improved air quality compared to conventional landscaping approaches.',
  },
  // Phase 26-28 - Misspelling & Colloquial Search Capture
  {
    question: 'Is this project also known as Nahar Chandivali or Nahar Chandi Wali?',
    answer: 'Yes. Nahar Chandi Valley is sometimes searched as "Nahar Chandivali", "Nahar Chandi Wali", or "Nahar Chandivalli". It is a premium residential development located in Chandivali, Mumbai, offering spacious 3 BHK residences.',
  },
  {
    question: 'Chandivali me under construction 3 BHK milta hai kya?',
    answer: 'Haan, Chandivali mein under-construction 3 BHK options available hain. Nahar Chandi Valley primarily offers spacious 3 BHK residences with modern layouts and phased pricing for end-use buyers.',
  },
  {
    question: 'Nahar Amrit Shakti ke paas kaunsa new project hai?',
    answer: 'Nahar Amrit Shakti ke paas Chandivali mein Nahar Chandi Valley ek newer under-construction project hai jo 3 BHK residences offer karta hai with modern amenities and contemporary layouts.',
  },
  {
    question: 'Powai ke paas 3 BHK ka budget kya hota hai?',
    answer: 'Powai ke adjacent area Chandivali mein 3 BHK homes ₹3.49 Cr se start hote hain (under-construction). Budget floor, configuration, aur release phase pe depend karta hai.',
  },
  // Phase 31 - Portal Intent Capture FAQs
  {
    question: 'I saw multiple Chandivali 3 BHK listings online — how do I compare them?',
    answer: 'Listings across platforms vary in configuration clarity, carpet definitions, and pricing transparency. Always compare RERA carpet, deck inclusion, and construction stage before deciding. This site provides verified details aligned with MahaRERA disclosures.',
  },
  {
    question: 'Is this project listed on property portals?',
    answer: 'Many buyers discover projects through online listings before visiting the official project site for verified plans, pricing logic, and current availability. This page provides direct, accurate information.',
  },
  {
    question: 'How do I know the price shown is accurate?',
    answer: 'Pricing varies by configuration, floor, and release phase. To maintain accuracy and avoid outdated figures, detailed pricing is shared on request after verification. Prices shown here reflect current under-construction stages.',
  },
];

interface FAQSectionProps {
  limit?: number;
  showViewAllLink?: boolean;
}

const FAQSection = ({ limit, showViewAllLink = false }: FAQSectionProps) => {
  const location = useLocation();
  const displayFaqs = limit ? faqs.slice(0, limit) : faqs;

  const handleFaqExpand = (question: string) => {
    analytics.faqExpand(question, location.pathname);
  };

  return (
    <section id="faq" className="section-padding bg-background">
      <div className="container mx-auto max-w-4xl">
        {/* Header */}
        <SectionHeading
          eyebrow="Frequently Asked Questions"
          title="Everything About Nahar Chandi Valley"
          lead="Find answers to common questions about Nahar Chandi Valley, pricing, location, amenities, and more."
          align="center"
          className="animate-fade-in-up"
        />

        {/* FAQ Accordion - First 3 items open by default for Google FAQ rich results eligibility */}
        <Accordion 
          type="multiple" 
          defaultValue={["item-0", "item-1", "item-2"]}
          className="space-y-4 mb-10"
        >
          {displayFaqs.map((faq, index) => (
            <AccordionItem
              key={index}
              value={`item-${index}`}
              className="bg-card rounded-lg shadow-sm border-none px-6 animate-fade-in-up"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <AccordionTrigger className="text-left font-sans font-semibold text-foreground hover:text-accent hover:no-underline py-5 text-[16px] md:text-[17px]">
                {faq.question}
              </AccordionTrigger>
              <AccordionContent className="faq-answer font-sans text-[15px] md:text-[16px] leading-[1.7] text-muted-foreground pb-5">
                {faq.answer}
                {faq.link && (
                  <a href={faq.link} className="text-accent hover:underline font-medium ml-1">
                    Learn more →
                  </a>
                )}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        {/* View All FAQs Link (when limited) */}
        {showViewAllLink && limit && faqs.length > limit && (
          <div className="text-center mb-8">
            <a 
              href="/faq" 
              className="inline-flex items-center gap-2 text-primary font-semibold hover:text-primary/80 transition-colors underline underline-offset-4"
            >
              View all {faqs.length} FAQs →
            </a>
          </div>
        )}

        {/* Internal Links for SEO */}
        <div className="flex flex-wrap gap-x-6 gap-y-3 justify-center text-sm animate-fade-in-up">
          <a href="/3-bhk-in-chandivali" className="text-accent hover:underline font-medium">
            3 BHK flats in Chandivali
          </a>
          <a href="/flats-near-nahar-amrit-shakti" className="text-accent hover:underline font-medium">
            Flats near Nahar Amrit Shakti
          </a>
          <a href="/under-construction-flats-chandivali" className="text-accent hover:underline font-medium">
            Under-construction flats Chandivali
          </a>
          <a href="/nahar-amrit-shakti-vs-nahar-chandivali" className="text-accent hover:underline font-medium">
            Nahar Amrit Shakti vs Chandi Valley
          </a>
          <a href="/compare" className="text-accent hover:underline font-medium">
            Compare with other projects
          </a>
          <a href="/price" className="text-accent hover:underline font-medium">
            View pricing & layouts
          </a>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
